import { React, useContext, useEffect, useState, useRef } from "react";
import { PageContext } from "../../App";
import { createAPIRequest } from "../WebUtils";
import { REQUEST_URL, STATUS_CHECK_INTERVAL } from "../Config";
import {
  formatBytes,
  getFileExtension,
  readPDFFileAsArrayBuffer,
  readEncryptedPDFFileAsArrayBuffer,
  loadFromURLAsBlob,
  saveFile,
  blobToFile,
  readPDFFile,
  renderPDFPage,
} from "../Utils";
import { encryptPDF } from "../PDFHelper/PDFRenderer";
import { PDFDocument } from "pdf-lib-plus-encrypt";
import {
  getSignedAWSS3URLToUploadFile,
  uploadFileToAWSS3Server,
  getSignedAWSS3URLToDownloadFile,
} from "../WebUtils";

function PDFToExcelUploadView(props) {
  // AppStatus.ts
  const AppState = {
    PROCESSING: "PROCESSING",
    PROCESSED: "PROCESSED",
    NONE: "NONE",
  };

  //get context
  const {
    selectedFiles,
    setSelectedFiles,
    setShowEditFileScreen,
    setStartProcessingFiles,
    setProgress,
  } = useContext(PageContext);

  //initialized flaf
  const initializedRef = useRef(false);
  // password input ref
  const inputRef = useRef(null);

  //on submit button clicked
  const [submitClicked, setSubmitClicked] = useState(false);
  //file upload details
  const [uploadDetails, setUploadDetails] = useState({
    state: AppState.NONE,
    outputBlob: null, //file received from server after processing
    isLocked: false, //check if locked
    message: "",
    password: "", //pdf password
    thumbnail: null, //thumbnail
    pageCount: 0,
  });

  //start
  useEffect(() => {
    //check
    if (initializedRef.current === false) {
      //set
      initializedRef.current = true;
      //upload files
      if (selectedFiles.length > 0) {
        //is encrypted
        const initializePDF = async () => {
          //get pdf information
          await getPDFInfo(selectedFiles[0], "");
        };

        //call initializePDF
        initializePDF();
      }
    }

    //check
    if (submitClicked === true) {
      //upload files
      if (selectedFiles.length > 0) {
        //set
        const selectedFile = selectedFiles[0];

        //unlock pdf file before processing
        if (uploadDetails.isLocked === false && uploadDetails.password !== "") {
          //unlock pdf
          const unlockPDF = async () => {
            //pdfdoc
            let pdfDoc = null;
            //try catch
            try {
              //set state
              setUploadDetails({
                ...uploadDetails,
                state: AppState.PROCESSING,
                message: "Reading file...",
              });
              //read pdf file
              let data = await readPDFFileAsArrayBuffer(selectedFiles[0]);
              // Load a PDF with form fields
              //if encrypted
              //proceed to ctch block
              pdfDoc = await PDFDocument.load(data);
            } catch (e) {
              //decrypy pdf using pdfjsLib
              //read pdf file
              //as pdf-lib does not decrypt encrypted we need pdfjslib to decrypt it
              let data = await readEncryptedPDFFileAsArrayBuffer(
                selectedFile,
                uploadDetails.password,
                (perct) => {
                  //set state
                  setUploadDetails({
                    ...uploadDetails,
                    state: AppState.PROCESSING,
                    message: `Extracted ${Math.floor(perct * 100)}%`,
                  });
                },
              );
              //check
              if (data === null) {
                //set state
                setUploadDetails({
                  ...uploadDetails,
                  state: AppState.NONE,
                  message: "Failed processing file.",
                });
                //return
                return;
              }

              //set state
              setUploadDetails({
                ...uploadDetails,
                state: AppState.PROCESSING,
                message: `Reading file...`,
              });

              // Load a PDF with form fields
              //if encrypted
              //proceed to ctch block
              pdfDoc = await PDFDocument.load(data);
            }

            //set state
            setUploadDetails({
              ...uploadDetails,
              state: AppState.PROCESSING,
              message: "",
            });

            //encrypt file
            //encrypt pdf
            //unsing password "" blank
            await encryptPDF(
              "UnLocked PDF",
              pdfDoc,
              "",
              {
                printable: true,
                cancopy: true,
              },
              (blob) => {
                //upload file
                processFile(
                  blobToFile(blob, selectedFiles[0].name),
                  REQUEST_URL,
                );
              },
            );
          };

          //unlock
          unlockPDF();
        } else {
          //set message
          setUploadDetails({
            ...uploadDetails,
            state: AppState.PROCESSING,
          });
          //upload file
          processFile(selectedFile, REQUEST_URL);
        }
      }
    } else {
      //hide progress
      setProgress(100);
    }

    //check
    if (selectedFiles.length === 0) {
      //reset
      resetScreen();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles, submitClicked]);

  //initialize pdf
  async function getPDFInfo(selectedFile, password) {
    //read pdf file
    await readPDFFile(selectedFile)
      .then(async (data) => {
        //thumbnail
        await renderPDFPage(data, password, "image/jpeg", 0.2, 0)
          .then(([img, numPages]) => {
            //set upload file details
            setUploadDetails({
              ...uploadDetails,
              state: AppState.NONE,
              isLocked: false,
              password: password,
              thumbnail: img,
              pageCount: numPages,
              message: "",
            });
          })
          .catch((e) => {
            //check
            if (e.name === "PasswordException") {
              //set upload file details
              setUploadDetails({
                ...uploadDetails,
                state: AppState.NONE,
                isLocked: true,
                password: "",
                message:
                  password !== "" ? "Incorrect password. Try again." : "",
              });
            } else {
              //set upload file details
              setUploadDetails({
                ...uploadDetails,
                state: AppState.NONE,
                isLocked: false,
                password: "",
                message: "Can not load file. Something went wrong.",
              });
            }
          });
      })
      .catch((e) => {
        console.log("Error: ", e);
      });
  }

  //reset screen
  //remove files info
  //empty selected files list
  const resetScreen = () => {
    //set flags
    setSelectedFiles([]);
    setUploadDetails({
      state: AppState.NONE,
      outputBlob: null, //file received from server after processing
      message: "",
    });
    setShowEditFileScreen(false);
    setStartProcessingFiles(false);
  };

  //check status from status from endpoint
  const checkRunpodJobStatus = async (url, job_id) => {
    //check runpod status
    //access token append automatically
    const response = await createAPIRequest(url, "POST", "application/json", {
      request_type: "STATUS_CHECK_PDF_TO_XLSX",
      job_id: job_id,
    });

    //check
    if (response === null) {
      //set message
      setUploadDetails({
        ...uploadDetails,
        state: AppState.NONE,
        message: "Network error. Check your internet connection and try again.",
      });
      //set
      setSubmitClicked(false);
      //return
      return;
    }

    //check success
    if (response.success === true) {
      //check status
      if (response.status === "COMPLETED") {
        //try catch
        try {
          //resobj
          const convObj = JSON.parse(response.message);
          //result image name
          const file_name = convObj.output_file;

          console.log("File name: ", file_name);

          //check
          if (file_name !== "") {
            //get file url
            const fileDownloadUrlResponse =
              await getSignedAWSS3URLToDownloadFile(url, file_name);
            //check
            if (
              fileDownloadUrlResponse !== null &&
              fileDownloadUrlResponse.success === true
            ) {
              //doewnload file
              const downloadedBlob = await loadFromURLAsBlob(
                fileDownloadUrlResponse.downloadUrl,
              );

              //set
              setUploadDetails({
                ...uploadDetails,
                state: AppState.PROCESSED,
                message: "Successfully converted PDF to XLSX.",
                outputBlob: downloadedBlob,
              });
              //set
              setSubmitClicked(false);
            } else {
              //set
              setUploadDetails({
                ...uploadDetails,
                state: AppState.NONE,
                message: "Sorry, failed downloading file.",
              });
              //set
              setSubmitClicked(false);
            }
          } else {
            //set
            setUploadDetails({
              ...uploadDetails,
              state: AppState.NONE,
              message: "Sorry, failed downloading file.",
            });
            //set
            setSubmitClicked(false);
          }

          console.log("Response: ", response);
        } catch {
          //set
          setUploadDetails({
            ...uploadDetails,
            state: AppState.NONE,
            message: "Sorry, failed processing file.",
          });
          //set
          setSubmitClicked(false);
        }
      } else if (
        response.status === "FAILED" ||
        response.status === "CANCELLED" ||
        response.status === "TIMED_OUT"
      ) {
        //set message
        setUploadDetails({
          ...uploadDetails,
          state: AppState.NONE,
          message:
            response.message === null
              ? "Sorry we couldn't process your files. This file has not been processed because it was damaged or we were unable to read it. Try again with a different file."
              : response.message,
        });
        //set
        setSubmitClicked(false);
      } else if (
        response.status === "IN_QUEUE" ||
        response.status === "RUNNING" ||
        response.status === "IN_PROGRESS"
      ) {
        //check api after 10 seconds
        setTimeout(() => {
          //callback
          checkRunpodJobStatus(url, job_id);
        }, STATUS_CHECK_INTERVAL);
      }
    } else {
      //set message
      setUploadDetails({
        ...uploadDetails,
        state: AppState.NONE,
        message:
          response.message === null
            ? "Sorry we couldn't process your files. This file has not been processed because it was damaged or we were unable to read it. Try again with a different file."
            : response.message,
      });
      //set
      setSubmitClicked(false);
    }
  };

  //send file to server as base64
  //process file
  //and get output
  const processFile = async (file, url) => {
    //check file size
    if (file.size < props.maxFileSize) {
      //check file type
      if (props.allowedFileExtensions.includes(getFileExtension(file.name))) {
        //set progress
        setProgress(30);

        //filename
        let uploadFileName = "";
        //get signed aws s3 url
        const response = await getSignedAWSS3URLToUploadFile(url, file);

        //set progress
        setProgress(50);

        //check
        if (response !== null && response.success === true) {
          //upload file
          const uploadUrl = response.uploadUrl;

          //check
          if (uploadUrl !== null) {
            //upload file
            const fileUploadResponse = await uploadFileToAWSS3Server(
              uploadUrl,
              file,
            );

            //check response
            if (fileUploadResponse !== null && fileUploadResponse.ok === true) {
              //filename
              //set upload filename
              uploadFileName = response.fileName;
            } else {
              //set message
              setUploadDetails({
                ...uploadDetails,
                state: "Sorry, failed uploading file. Please try again later.",
              });
              //set
              setSubmitClicked(false);
              //retutn
              return;
            }
          } else {
            //set
            setSubmitClicked(false);
            //retutn
            return;
          }
        } else {
          //set message
          setUploadDetails({
            ...uploadDetails,
            state:
              response === null
                ? "Network error. Check your internet connection and try again."
                : response.message,
          });
          //set
          setSubmitClicked(false);
          //set progress
          //retutn
          return;
        }

        //apin request pdf to xlsx
        //access token append automatically
        const responsePDFToXLSX = await createAPIRequest(
          url,
          "POST",
          "application/json",
          {
            request_type: "PDF_TO_XLSX_CONVERTER",
            upload_file_name: uploadFileName,
            job_id: null,
          },
        );

        //set progress
        setProgress(70);

        //check
        if (responsePDFToXLSX === null) {
          //set message
          setUploadDetails({
            ...uploadDetails,
            state: AppState.NONE,
            message:
              "Network error. Check your internet connection and try again.",
          });
          //set
          setSubmitClicked(false);
          //return
          return;
        }

        //check
        //if failed
        if (responsePDFToXLSX.success === false) {
          //set message
          setUploadDetails({
            ...uploadDetails,
            state: AppState.NONE,
            message: responsePDFToXLSX.message,
          });
          //set
          setSubmitClicked(false);
        } else {
          //set token in local storage
          localStorage.setItem("access_token", responsePDFToXLSX.token);
          //set job_id
          const job_id = responsePDFToXLSX.job_id;
          //check response from runpod server
          //when humanize text is completed
          if (
            responsePDFToXLSX.status === "FAILED" ||
            responsePDFToXLSX.status === "CANCELLED" ||
            responsePDFToXLSX.status === "TIMED_OUT" ||
            job_id === null
          ) {
            //set message
            setUploadDetails({
              ...uploadDetails,
              state: AppState.NONE,
              message:
                responsePDFToXLSX.message === null
                  ? "Sorry we couldn't process your files. This file has not been processed because it was damaged or we were unable to read it. Try again with a different file."
                  : responsePDFToXLSX.message,
            });
            //set
            setSubmitClicked(false);
          } else {
            //check status for job_id
            checkRunpodJobStatus(url, job_id);
          }
        }
      }
    } else {
      //set message
      setUploadDetails({
        ...uploadDetails,
        state: AppState.NONE,
        message: `Sorry, we can’t process this file because it exceeds the maximum size limit. Please upload a smaller file. The maximum allowed size is - ${formatBytes(props.maxFileSize)}`,
      });
      //set
      setSubmitClicked(false);
    }
  };

  return (
    <div
      className="position-relative container bg-light p-5 rounded-2"
      style={{ marginTop: "6rem" }}
    >
      {/* Reset button */}
      <button
        className="position-absolute btn btn-transparent"
        style={{ top: "10px", right: "10px" }}
        onClick={resetScreen}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="26"
          height="26"
          fill="#000000"
          className="bi bi-arrow-clockwise"
          viewBox="0 0 16 16"
        >
          <path
            fillRule="evenodd"
            d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"
          />
          <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466" />
        </svg>
      </button>

      {/* original file */}
      <div className="position-relative text-center">
        {/* title */}
        <div className="text-center">
          <label className="fs-3 text-dark lead fw-bold">Upload PDF file</label>
        </div>

        {/* Image */}
        <img
          src={
            uploadDetails.thumbnail !== null
              ? uploadDetails.thumbnail
              : "./images/file_icon.png"
          }
          className={`mt-5 ${uploadDetails.thumbnail === null ? "" : "border"}`}
          alt="pdf_to_xlsx"
          style={{
            width: "150px",
            height: "auto",
            opacity: uploadDetails.thumbnail !== null ? 1.0 : 0.3,
          }}
        />

        {/* Name */}
        <div className="d-flex mt-4 align-items-center justify-content-center">
          {/* Label */}
          <label className="fs-4 text-secondary">
            {selectedFiles.length > 0 ? selectedFiles[0].name : "unknown"}
          </label>

          {/* Progress */}
          <div
            className={`spinner-border text-info ms-3 ${uploadDetails.state === AppState.PROCESSING ? "" : "d-none"}`}
            role="status"
          ></div>
        </div>

        {/* File size */}
        <div className="mt-1 align-items-center justify-content-center">
          {/* label */}
          <label className="text-secondary" type="text">
            Size:{" "}
            {selectedFiles.length > 0
              ? formatBytes(selectedFiles[0].size)
              : formatBytes(0)}
          </label>
        </div>

        {/* Pages */}
        <div className="mt-1 align-items-center justify-content-center">
          {/* pages */}
          <label className="text-secondary">
            Pages: {uploadDetails.pageCount}
          </label>
        </div>

        {/* Unlock pdf */}
        {/* Convert button */}
        {uploadDetails.isLocked === true && uploadDetails.password === "" ? (
          <div className="mt-4">
            {/* password input field */}
            <input
              className="form-control border border-primary mx-auto"
              style={{ width: "200px" }}
              placeholder="Enter password..."
              id="pdf_passoword"
              type="password"
              ref={inputRef}
            />
            {/* Unlock button */}
            <button
              className="btn btn-danger mt-3 fs-3 px-4 rounded-2"
              onClick={async (e) => {
                //checf
                if (inputRef === null) {
                  return;
                }

                //check if correct password
                //store password
                await getPDFInfo(selectedFiles[0], inputRef.current.value);
              }}
            >
              Unlock PDF
            </button>
          </div>
        ) : uploadDetails.state !== AppState.PROCESSED ? (
          <div>
            {/* max file size */}
            <div className="text-center mt-4">
              <label className="text-secondary">
                *Max file size allowed {formatBytes(props.maxFileSize)}
              </label>
            </div>
            {/* Convert button */}
            <button
              className="btn btn-primary mt-2 fs-3 px-4 rounded-2"
              disabled={submitClicked}
              onClick={(e) => {
                //set
                setSubmitClicked(true);
              }}
            >
              Convert to .xlsx
            </button>
          </div>
        ) : (
          <div className="mt-4">
            {/* max file size */}
            <div className="text-center mt-4">
              <label className="text-secondary">
                We respect your privacy, your files are not saved on our servers
                and are automatically removed after processing.
              </label>
            </div>
            {/* Download button */}
            <button
              className="btn btn-success mt-3 fs-3 px-4 rounded-2"
              onClick={async (e) => {
                //download
                saveFile(uploadDetails.outputBlob, "pdf_to_excel.xlsx");
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-download me-2"
                viewBox="0 0 16 16"
              >
                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
              </svg>
              Download
            </button>
          </div>
        )}

        {/* Message */}
        <div className={`mt-5 ${uploadDetails.message === "" ? "d-none" : ""}`}>
          <label className="lead fs-2 text-dark fw-italic">
            {uploadDetails.message}
          </label>
        </div>
      </div>
    </div>
  );
}

export default PDFToExcelUploadView;
